/**
 * Placeholder-art loader (CP21). Sprite files are simple LEGO-brick-ish
 * flat-colored SVGs under `assets/sprites/`, described by
 * `assets/manifest.json`. Swappable for real seed-locked stacked-prompt
 * art (Brief §9/§10) later with zero game-code changes: replace the SVG
 * content (same manifest key/filename) or point a manifest `file` entry at
 * a new file under `assets/sprites/` - this module resolves whatever the
 * manifest points to via a glob, it never hardcodes a file list.
 *
 * SVGs are inlined as base64 data URIs (via Vite's `?raw` glob import)
 * rather than fetched as separate runtime requests: they end up inside the
 * JS bundle, which the PWA's precache (vite-plugin-pwa/Workbox) already
 * covers, so offline boot needs no extra runtime-caching rule for a
 * separate assets/ static path. Constraint: sprite SVG source must stay
 * ASCII-only (no literal curly quotes, accented characters, etc.) because
 * `btoa` only encodes Latin1 - fine for these flat placeholder shapes;
 * revisit if real art ever needs raw SVG with non-ASCII text content.
 */
import manifestJson from '../../assets/manifest.json';

export interface SpriteManifestEntry {
  file: string;
  width: number;
  height: number;
  description: string;
}

interface SpriteManifestFile {
  version: number;
  note: string;
  sprites: Record<string, SpriteManifestEntry>;
}

export interface ResolvedSprite extends SpriteManifestEntry {
  key: string;
  /** `data:image/svg+xml;base64,...` URL, ready for `scene.load.svg(key, url, {width, height})`. */
  url: string;
}

const spriteSource = import.meta.glob<string>('/assets/sprites/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
});

function toDataUrl(svgText: string): string {
  return `data:image/svg+xml;base64,${btoa(svgText)}`;
}

function resolveSvgUrl(file: string): string {
  const path = `/assets/sprites/${file}`;
  const raw = spriteSource[path];
  if (!raw) {
    throw new Error(
      `assets/manifest.json references "${file}" but no matching file was found under assets/sprites/ (looked for ${path})`,
    );
  }
  return toDataUrl(raw);
}

const manifest = manifestJson as SpriteManifestFile;

/** Every manifest sprite, resolved to a loadable data-URI. Computed once at module load. */
export function getSpriteManifest(): Record<string, ResolvedSprite> {
  const sprites: Record<string, ResolvedSprite> = {};
  for (const [key, entry] of Object.entries(manifest.sprites)) {
    sprites[key] = { ...entry, key, url: resolveSvgUrl(entry.file) };
  }
  return sprites;
}
